import React from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/shared/auth';
import { bff } from '@/lib/http/bff';
import Login from '@/features/system/auth/Login';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function Device() {
  useDocumentTitle('Device');
  const location = useLocation();
  const [search] = useSearchParams();
  const { user, loading, refresh } = useAuth();

  const inputRef = React.useRef<HTMLInputElement | null>(null);

  const [userCode, setUserCode] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const sanitizeUserCode = React.useCallback((raw: string) => {
    const trimmed = String(raw || '').trim();
    // Only allow [A-Za-z0-9-] per requirement; treat as untrusted input.
    const safe = trimmed.replace(/[^A-Za-z0-9-]/g, '');
    // Codes are case-insensitive; normalize to uppercase for UX.
    return safe.toUpperCase();
  }, []);

  const authorize = React.useCallback(async (code: string) => {
    setError(null);
    setSubmitting(true);
    try {
      // If a `target` param is present, the device code lives on a remote BFF.
      // Pass target_bff_url so the local BFF proxies the approval.
      const target = search.get('target') || '';
      const payload: Record<string, string> = { user_code: code };
      if (target) payload.target_bff_url = target;

      await bff.post('/device/authorize', payload, { timeoutMs: 30_000 });
      setDone(true);

      // Best-effort: browsers only allow scripts to close windows they opened.
      // If PACDaemon opened the browser via OS shell, this will usually be blocked.
      queueMicrotask(() => {
        try {
          if (window.opener) window.close();
        } catch {
          // ignore
        }
      });
    } catch (err: any) {
      // Map known low-level network/abort errors to user-facing copy so the
      // banner never shows raw browser strings like "signal is aborted
      // without reason" (the default DOMException message when fetch is
      // aborted by our client-side timeout).
      const name = err?.name;
      const status = err?.status;
      const rawMessage: string = err?.body?.detail
        || err?.body?.error_description
        || err?.body?.error
        || err?.message
        || '';
      const friendly = (() => {
        if (name === 'TimeoutError' || name === 'AbortError' || /aborted/i.test(rawMessage)) {
          return 'The authorization service did not respond in time. Please check your connection and try again.';
        }
        if (status === 0 || /Failed to fetch|NetworkError/i.test(rawMessage)) {
          return 'Could not reach the authorization service. Please try again in a moment.';
        }
        if (status >= 500) {
          return 'The authorization service is temporarily unavailable. Please try again shortly.';
        }
        if (status === 404 || /invalid_grant|expired/i.test(rawMessage)) {
          return 'This device code is no longer valid. Please restart the device pairing flow.';
        }
        return rawMessage || 'Authorization failed. Please try again.';
      })();
      setError(friendly);
    } finally {
      setSubmitting(false);
    }
  }, []);

  const code = search.get('user_code') || '';

  // Ensure we attempt to load session if arriving directly.
  React.useEffect(() => {
    if (!user && !loading) void refresh();
  }, [user, loading, refresh]);

  React.useEffect(() => {
    // Hooks must run unconditionally; gate behavior inside the effect.
    if (loading) return;
    if (!user) return;
    if (!code) return;
    const sanitized = sanitizeUserCode(code);
    if (!sanitized) return;

    setUserCode(sanitized);

    // Optional: focus the input when a code is present.
    queueMicrotask(() => inputRef.current?.focus());
  }, [code, loading, sanitizeUserCode, user]);

  if (loading) return null;

  // Not logged in → render login in-place (keep URL stable).
  if (!user) {
    return <Login inline from={location} />;
  }

  // Minimal placeholder: the important part for now is the /device → /login redirect.
  return (
    <div className="h-screen overflow-auto bg-black flex items-center justify-center">
      <div className="w-full max-w-lg p-6 pac-glass rounded-lg text-white">
        <h1 className="text-2xl mb-2">Authorize Device</h1>
        <p className="text-sm text-neutral-300 mb-4">
          You are logged in. Continue device authorization here.
        </p>

        {error && <div className="text-sm text-red-400 mb-3">{error}</div>}

        {done && (
          <div className="text-sm text-neutral-300 mb-3 flex items-center justify-between gap-3">
            <div>Authorized. You can close this tab.</div>
            <button
              type="button"
              className="pac-glass-btn px-3 py-2 rounded-lg text-white font-medium transition-colors"
              onClick={() => {
                try { window.close(); } catch {}
              }}
            >
              Close tab
            </button>
          </div>
        )}

        <form
          onSubmit={async (e) => {
            e.preventDefault();
            const sanitized = sanitizeUserCode(userCode);
            setUserCode(sanitized);
            if (!sanitized) return;
            await authorize(sanitized);
          }}
          className="space-y-3"
        >
          <div>
            <label className="block text-sm mb-1">User Code</label>
            <input
              ref={inputRef}
              value={userCode}
              onChange={(e) => setUserCode(sanitizeUserCode(e.target.value))}
              className="w-full px-3 py-2 rounded-lg pac-glass-input placeholder-neutral-400 disabled:opacity-50"
              placeholder="ABCD-1234"
              spellCheck={false}
              disabled={submitting || done}
            />
          </div>

          <button
            type="submit"
            disabled={submitting || done || !userCode}
            className="w-full bg-emerald-800 hover:bg-emerald-700 px-4 py-2 rounded-lg text-white font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            aria-busy={submitting}
          >
            {done ? 'Authorized' : (submitting ? 'Authorizing…' : 'Authorize device')}
          </button>
        </form>
      </div>
    </div>
  );
}
